import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {AgencyRepository} from '../../repositories/AgencyRepository.ts'
import {AuthenticationRepository} from '../../repositories/AuthenticationRepository.ts'
import {GetAgencyObservationsUseCase} from '../../domain/useCases/agencies/GetAgencyObservationsUseCase.ts'
import {AddAgencyObservationUseCase} from '../../domain/useCases/agencies/AddAgencyObservationUseCase.ts'
import {UpdateAgencyObservationUseCase} from '../../domain/useCases/agencies/UpdateAgencyObservationUseCase.ts'
import {DeleteAgencyObservationUseCase} from '../../domain/useCases/agencies/DeleteAgencyObservationUseCase.ts'
import {AgencyObservation} from '../../domain/entities/Agency.ts'
import {ConfirmDialog} from '../dialogs/ConfirmDialog.tsx'
import {Dialog} from '../Dialog.tsx'
import {Button} from '../Button.tsx'
import {initialsOf} from '../../utils/initials.ts'
import IconSpinner from '../../assets/icons/spinner.svg?react'
import IconTrash from '../../assets/icons/trash.svg?react'
import IconEdit from '../../assets/icons/edit.svg?react'

// Mirrors MAX_OBSERVATION_LENGTH in colab-api's AgencyController.
const MAX_LENGTH = 5000

const agencyRepo = new AgencyRepository(new AuthenticationRepository())
const getObservationsUseCase = new GetAgencyObservationsUseCase(agencyRepo)
const addObservationUseCase = new AddAgencyObservationUseCase(agencyRepo)
const updateObservationUseCase = new UpdateAgencyObservationUseCase(agencyRepo)
const deleteObservationUseCase = new DeleteAgencyObservationUseCase(agencyRepo)

interface AgencyObservationsSectionProps {
    agencyId: string
}

// Internal 51trips notes about the agency (calls, incidents, agreements…),
// shown newest first. Admin-only: the backend rejects anyone else.
export const AgencyObservationsSection: React.FC<AgencyObservationsSectionProps> = ({agencyId}) => {
    const {t} = useTranslation('dashboard')
    const [observations, setObservations] = useState<AgencyObservation[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const [draft, setDraft] = useState('')
    const [isAdding, setIsAdding] = useState(false)

    const [editingId, setEditingId] = useState<string | null>(null)
    const [editText, setEditText] = useState('')
    const [isUpdating, setIsUpdating] = useState(false)

    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
    const [deletingId, setDeletingId] = useState<string | null>(null)
    const confirmDeleteDialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)

    useEffect(() => {
        if (!agencyId) return
        setIsLoading(true)
        getObservationsUseCase.execute(agencyId)
            .then(setObservations)
            .catch((err) => {
                console.error('Error loading agency observations:', err)
                setError(t('agencyObservations.loadError'))
            })
            .finally(() => setIsLoading(false))
    }, [agencyId, t])

    const handleAdd = () => {
        if (!draft.trim() || isAdding) return
        setIsAdding(true)
        setError(null)
        addObservationUseCase.execute(agencyId, draft)
            .then((created) => {
                setObservations((prev) => [created, ...prev])
                setDraft('')
            })
            .catch((err) => {
                console.error('Error adding agency observation:', err)
                setError(t('agencyObservations.saveError'))
            })
            .finally(() => setIsAdding(false))
    }

    const startEdit = (observation: AgencyObservation) => {
        setEditingId(observation.id)
        setEditText(observation.text)
        setError(null)
    }

    const handleUpdate = () => {
        if (!editingId || !editText.trim() || isUpdating) return
        const id = editingId
        setIsUpdating(true)
        setError(null)
        updateObservationUseCase.execute(agencyId, id, editText)
            .then((updated) => {
                setObservations((prev) => prev.map((o) => o.id === id ? updated : o))
                setEditingId(null)
            })
            .catch((err) => {
                console.error('Error updating agency observation:', err)
                setError(t('agencyObservations.saveError'))
            })
            .finally(() => setIsUpdating(false))
    }

    const askDelete = (id: string) => {
        setPendingDeleteId(id)
        confirmDeleteDialog.current?.open()
    }

    // ConfirmDialog closes itself as soon as onConfirm returns truthy, so the
    // request runs in the background and the row shows its own spinner.
    const confirmDelete = () => {
        if (!pendingDeleteId) return false
        const id = pendingDeleteId
        setDeletingId(id)
        setError(null)
        deleteObservationUseCase.execute(agencyId, id)
            .then(() => setObservations((prev) => prev.filter((o) => o.id !== id)))
            .catch((err) => {
                console.error('Error deleting agency observation:', err)
                setError(t('agencyObservations.deleteError'))
            })
            .finally(() => {
                setDeletingId(null)
                setPendingDeleteId(null)
            })
        return true
    }

    // Cmd/Ctrl+Enter submits, plain Enter keeps adding lines.
    const submitOnModEnter = (submit: () => void) => (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            submit()
        }
    }

    return (
        <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-5 flex flex-col gap-4">
            <div className="flex flex-col gap-0.5">
                <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('agencyObservations.sectionTitle')}</span>
                <span className="text-xs text-neutralBlack-400">{t('agencyObservations.sectionHelp')}</span>
            </div>

            <div className="flex flex-col gap-2">
                <label htmlFor="newObservation" className="sr-only">{t('agencyObservations.newPlaceholder')}</label>
                <textarea
                    id="newObservation"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={submitOnModEnter(handleAdd)}
                    rows={3}
                    maxLength={MAX_LENGTH}
                    placeholder={t('agencyObservations.newPlaceholder')}
                    className="w-full rounded-xl border border-neutralBlack-200 px-3.5 py-2.5 text-sm resize-y focus:outline-none focus:border-neutralBlack-900 shadow-sm"
                />
                <div className="flex items-center gap-3">
                    <span className="text-xs text-neutralBlack-400">{t('agencyObservations.shortcutHint')}</span>
                    <span className="flex-1"/>
                    <Button variant="primary" size="small" className="max-h-[36px]"
                            disabled={isAdding || !draft.trim()}
                            onClick={handleAdd}>
                        {isAdding ? <IconSpinner className="size-4"/> : t('agencyObservations.add')}
                    </Button>
                </div>
            </div>

            {error && (
                <div role="alert" className="text-sm text-danger bg-danger/10 rounded-xl px-3.5 py-2.5">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="flex justify-center py-6">
                    <IconSpinner className="size-6"/>
                </div>
            ) : observations.length === 0 ? (
                <div className="text-center py-8 text-sm text-neutralBlack-500">
                    {t('agencyObservations.noObservationsYet')}
                </div>
            ) : (
                <ul className="flex flex-col">
                    {observations.map((observation) => (
                        <li key={observation.id} className="flex gap-3 py-3.5 border-b border-neutralBlack-100 last:border-0 group">
                            <span className="w-8 h-8 flex-none rounded-full bg-neutralBlack-100 flex items-center justify-center text-[11px] font-semibold text-neutralBlack-700">
                                {initialsOf(observation.authorName ?? observation.authorEmail)}
                            </span>
                            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[13.5px] font-medium text-neutralBlack-900 truncate">
                                        {observation.authorName || observation.authorEmail || '—'}
                                    </span>
                                    <span className="text-xs text-neutralBlack-500">
                                        {formatDateTime(observation.createdAt)}
                                        {observation.updatedAt ? ` · ${t('agencyObservations.edited')}` : ''}
                                    </span>
                                    <span className="flex-1"/>
                                    {editingId !== observation.id && (
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                            <button
                                                type="button"
                                                title={t('edit')}
                                                aria-label={t('edit')}
                                                onClick={() => startEdit(observation)}
                                                className="w-[26px] h-[26px] flex-none flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-neutralBlack-100 hover:text-neutralBlack-900"
                                            >
                                                <IconEdit className="size-3"/>
                                            </button>
                                            <button
                                                type="button"
                                                title={t('delete')}
                                                aria-label={t('delete')}
                                                disabled={deletingId === observation.id}
                                                onClick={() => askDelete(observation.id)}
                                                className="w-[26px] h-[26px] flex-none flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-danger/10 hover:text-danger disabled:opacity-40"
                                            >
                                                {deletingId === observation.id ? <IconSpinner className="size-3"/> : <IconTrash className="size-3"/>}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {editingId === observation.id ? (
                                    <div className="flex flex-col gap-2">
                                        <textarea
                                            aria-label={t('edit')}
                                            value={editText}
                                            onChange={(e) => setEditText(e.target.value)}
                                            onKeyDown={submitOnModEnter(handleUpdate)}
                                            rows={3}
                                            maxLength={MAX_LENGTH}
                                            autoFocus
                                            className="w-full rounded-xl border border-neutralBlack-200 px-3.5 py-2.5 text-sm resize-y focus:outline-none focus:border-neutralBlack-900 shadow-sm"
                                        />
                                        <div className="flex gap-2">
                                            <Button variant="primary" size="small" className="max-h-[32px]"
                                                    disabled={isUpdating || !editText.trim()}
                                                    onClick={handleUpdate}>
                                                {isUpdating ? <IconSpinner className="size-4"/> : t('agencyObservations.save')}
                                            </Button>
                                            <Button variant="secondary" size="small" className="max-h-[32px]"
                                                    disabled={isUpdating}
                                                    onClick={() => setEditingId(null)}>
                                                {t('cancel')}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-neutralBlack-800 whitespace-pre-wrap break-words">
                                        {observation.text}
                                    </p>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <ConfirmDialog
                ref={confirmDeleteDialog}
                onConfirm={confirmDelete}
                title={t('agencyObservations.deleteConfirmTitle')}
                message={t('agencyObservations.deleteConfirmMessage')}
                confirmLabel={t('delete')}
                cancelLabel={t('cancel')}
            />
        </section>
    )
}

function formatDateTime(timestamp: number): string {
    return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(timestamp))
}
